/**
 * useIdleTimer Hook
 * 
 * Tracks user activity and manages session timeout with cross-tab sync.
 * Uses localStorage + storage events for multi-tab coordination,
 * and throttled writes to avoid performance overhead.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// =============================================================================
// Constants
// =============================================================================

const SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000;   // 2 hours total session time
const WARNING_BEFORE_MS = 2 * 60 * 1000;         // Warn 2 minutes before expiration
const WARNING_AT_MS = SESSION_TIMEOUT_MS - WARNING_BEFORE_MS; // 2 hours minus 2 minutes
const THROTTLE_MS = 1000;                         // Throttle localStorage writes
const CHECK_INTERVAL_MS = 1000;                    // Check idle status every second
const STORAGE_KEY = 'lastActiveTime';

// =============================================================================
// Throttle Utility
// =============================================================================

function throttle<T extends (...args: unknown[]) => void>(fn: T, limitMs: number): T {
  let lastCall = 0;
  return ((...args: unknown[]) => {
    const now = Date.now();
    if (now - lastCall >= limitMs) {
      lastCall = now;
      fn(...args);
    }
  }) as T;
}

// =============================================================================
// Types
// =============================================================================

interface UseIdleTimerOptions {
  enabled: boolean;
  onTimeout: () => void;
}

interface UseIdleTimerReturn {
  isWarning: boolean;
  remainingSeconds: number;
  resetTimer: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useIdleTimer({ enabled, onTimeout }: UseIdleTimerOptions): UseIdleTimerReturn {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(120);

  const lastActiveRef = useRef(Date.now());
  const isWindowFocusedRef = useRef(document.hasFocus());
  const onTimeoutRef = useRef(onTimeout);
  const hasTimedOutRef = useRef(false);

  onTimeoutRef.current = onTimeout;

  // ---------------------------------------------------------------------------
  // Reset Timer
  // ---------------------------------------------------------------------------

  const resetTimer = useCallback(() => {
    const now = Date.now();
    lastActiveRef.current = now;
    hasTimedOutRef.current = false;
    setIsWarning(false);
    setRemainingSeconds(120);
    try {
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // localStorage may be unavailable (private browsing, quota exceeded)
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Activity Recording (throttled)
  // ---------------------------------------------------------------------------

  const recordActivity = useCallback(() => {
    if (!isWindowFocusedRef.current) return;
    const now = Date.now();
    lastActiveRef.current = now;
    try {
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // Silently ignore localStorage errors
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Effects (only when enabled)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!enabled) return;

    hasTimedOutRef.current = false;

    // Initialize localStorage with current time
    const now = Date.now();
    lastActiveRef.current = now;
    try {
      localStorage.setItem(STORAGE_KEY, now.toString());
    } catch {
      // Silently ignore
    }

    // --- Throttled activity handler ---
    const throttledRecord = throttle(recordActivity, THROTTLE_MS);

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'] as const;
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledRecord, { passive: true });
    });

    // --- Focus / Blur ---
    const handleFocus = () => {
      isWindowFocusedRef.current = true;
    };

    const handleBlur = () => {
      isWindowFocusedRef.current = false;
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // --- Cross-tab sync via storage event ---
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const remoteTime = parseInt(e.newValue, 10);
        if (!isNaN(remoteTime) && remoteTime > lastActiveRef.current) {
          lastActiveRef.current = remoteTime;
        }
      }
      // Another tab logged out (removed the key)
      if (e.key === STORAGE_KEY && e.newValue === null) {
        if (!hasTimedOutRef.current) {
          hasTimedOutRef.current = true;
          onTimeoutRef.current();
        }
      }
    };

    window.addEventListener('storage', handleStorage);

    // --- Check loop ---
    const intervalId = setInterval(() => {
      if (hasTimedOutRef.current) return;

      const idleMs = Date.now() - lastActiveRef.current;

      if (idleMs >= SESSION_TIMEOUT_MS) {
        hasTimedOutRef.current = true;
        setIsWarning(false);
        onTimeoutRef.current();
        return;
      }

      if (idleMs >= WARNING_AT_MS) {
        const msLeft = SESSION_TIMEOUT_MS - idleMs;
        setIsWarning(true);
        setRemainingSeconds(Math.max(0, Math.ceil(msLeft / 1000)));
      } else {
        setIsWarning(false);
        setRemainingSeconds(120);
      }
    }, CHECK_INTERVAL_MS);

    // --- Cleanup ---
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledRecord);
      });
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('storage', handleStorage);
      clearInterval(intervalId);
    };
  }, [enabled, recordActivity]);

  // When disabled, return safe defaults
  if (!enabled) {
    return { isWarning: false, remainingSeconds: 120, resetTimer: () => {} };
  }

  return { isWarning, remainingSeconds, resetTimer };
}
